import {
  type CollabFieldGrouping,
  type CollabFieldGroupingJSON,
  type CollabGrouping,
  type CollabGroupingJSON,
  type MutableCollabFieldGrouping,
  type MutableCollabGrouping,
  CollabGroupSortType
} from '@databases/model/views/view-grouping';
import * as Y from 'yjs';

import { SynchronizedRecord } from '../helpers/synchronized-record';
import { createUniqueStringArray } from '../helpers/utils';
import { type YUniqueArray } from '../helpers/y-unique-array';

export const GroupingProperties = {
  groupBy: 'gb',
  subgroupBy: 'sb'
};

export const FieldGroupingProperties = {
  fieldId: 'fid',
  sortType: 'st',
  manualGroupOrder: 'mgo'
};

enum YGroupSortType {
  ASCENDING = 'a',
  DESCENDING = 'd',
  MANUAL = 'm'
}

const GroupSortTypeMap: Record<CollabGroupSortType, YGroupSortType> = {
  [CollabGroupSortType.ASCENDING]: YGroupSortType.ASCENDING,
  [CollabGroupSortType.DESCENDING]: YGroupSortType.DESCENDING,
  [CollabGroupSortType.MANUAL]: YGroupSortType.MANUAL
};

const YGroupSortTypeMap: Record<YGroupSortType, CollabGroupSortType> = {
  [YGroupSortType.ASCENDING]: CollabGroupSortType.ASCENDING,
  [YGroupSortType.DESCENDING]: CollabGroupSortType.DESCENDING,
  [YGroupSortType.MANUAL]: CollabGroupSortType.MANUAL
};

const YGroupSortTypeValues = Object.values(YGroupSortType);

export class YFieldGrouping
  extends SynchronizedRecord
  implements CollabFieldGrouping, MutableCollabFieldGrouping
{
  #manualGroupOrder: YUniqueArray<string>;

  constructor(input: Y.Map<any>) {
    super(input);

    this.#manualGroupOrder = createUniqueStringArray(
      input.get(FieldGroupingProperties.manualGroupOrder)
    );
  }

  get fieldId(): string {
    return this.getString(FieldGroupingProperties.fieldId, '');
  }

  set fieldId(fieldId: string) {
    this.set(FieldGroupingProperties.fieldId, fieldId);
  }

  get sortType(): CollabGroupSortType {
    return YGroupSortTypeMap[
      this.getStringEnum(
        FieldGroupingProperties.sortType,
        YGroupSortTypeValues,
        YGroupSortType.ASCENDING
      )
    ];
  }

  set sortType(type: CollabGroupSortType) {
    this.set(FieldGroupingProperties.sortType, GroupSortTypeMap[type]);
  }

  get manualGroupOrder(): string[] {
    return this.#manualGroupOrder.toArray();
  }

  set manualGroupOrder(order: string[]) {
    const valueRef = new Y.Array();
    this.set(FieldGroupingProperties.manualGroupOrder, valueRef);
    this.#manualGroupOrder = createUniqueStringArray(valueRef);
    for (const value of order) {
      this.#manualGroupOrder.push(value);
    }
  }

  /**
   * Adds a group before another group.
   *
   * If `groupKey` is already in the list, it is moved to the new position.
   * If `beforeGroupKey` is not in the list, the group is added to the end.
   */
  addGroupBefore(groupKey: string, beforeGroupKey: string): void {
    this.#manualGroupOrder.addBefore([groupKey], beforeGroupKey);
  }

  toJSON(): CollabFieldGroupingJSON {
    return {
      fieldId: this.fieldId,
      sortType: this.sortType,
      manualGroupOrder: this.manualGroupOrder
    };
  }

  static fromJSON(json: CollabFieldGroupingJSON): YFieldGrouping {
    return new YFieldGrouping(
      new Y.Map([
        [FieldGroupingProperties.fieldId, json.fieldId],
        [FieldGroupingProperties.sortType, GroupSortTypeMap[json.sortType]],
        [FieldGroupingProperties.manualGroupOrder, Y.Array.from(json.manualGroupOrder)]
      ])
    );
  }
}

export class YGrouping
  extends SynchronizedRecord
  implements CollabGrouping, MutableCollabGrouping
{
  #groupBy: YFieldGrouping | undefined;
  #subgroupBy: YFieldGrouping | undefined;

  constructor(input: Y.Map<any>) {
    super(input);
    input.observe(this.onValueChanged);

    const groupByRef = input.get(GroupingProperties.groupBy);
    if (groupByRef instanceof Y.Map) {
      this.#groupBy = new YFieldGrouping(groupByRef);
    }

    const subgroupByRef = input.get(GroupingProperties.subgroupBy);
    if (subgroupByRef instanceof Y.Map) {
      this.#subgroupBy = new YFieldGrouping(subgroupByRef);
    }
  }

  private onValueChanged = (e: Y.YMapEvent<any>) => {
    if (e.keysChanged.has(GroupingProperties.groupBy)) {
      const val = this.data.get(GroupingProperties.groupBy);
      if (val instanceof Y.Map) {
        this.#groupBy = new YFieldGrouping(val);
      } else {
        this.#groupBy = undefined;
      }
    }

    if (e.keysChanged.has(GroupingProperties.subgroupBy)) {
      const val = this.data.get(GroupingProperties.subgroupBy);
      if (val instanceof Y.Map) {
        this.#subgroupBy = new YFieldGrouping(val);
      } else {
        this.#subgroupBy = undefined;
      }
    }
  };

  get groupBy() {
    return this.#groupBy;
  }

  get subgroupBy() {
    return this.#subgroupBy;
  }

  setGroupBy(fieldGrouping: CollabFieldGroupingJSON): MutableCollabFieldGrouping {
    const yFieldGrouping = YFieldGrouping.fromJSON(fieldGrouping);
    this.set(GroupingProperties.groupBy, yFieldGrouping.data);
    this.#groupBy = yFieldGrouping;
    return yFieldGrouping;
  }

  groupByField(fieldId: string, manualGroupOrder?: string[]): MutableCollabFieldGrouping {
    return this.setGroupBy({
      fieldId,
      sortType: manualGroupOrder
        ? CollabGroupSortType.MANUAL
        : CollabGroupSortType.ASCENDING,
      manualGroupOrder: manualGroupOrder ?? []
    });
  }

  setSubgroupBy(fieldGrouping: CollabFieldGroupingJSON): MutableCollabFieldGrouping {
    const yFieldGrouping = YFieldGrouping.fromJSON(fieldGrouping);
    this.set(GroupingProperties.subgroupBy, yFieldGrouping.data);
    this.#subgroupBy = yFieldGrouping;
    return yFieldGrouping;
  }

  subgroupByField(
    fieldId: string,
    manualGroupOrder?: string[]
  ): MutableCollabFieldGrouping {
    return this.setSubgroupBy({
      fieldId,
      sortType: manualGroupOrder
        ? CollabGroupSortType.MANUAL
        : CollabGroupSortType.ASCENDING,
      manualGroupOrder: manualGroupOrder ?? []
    });
  }

  clear(): void {
    this.#groupBy = undefined;
    this.#subgroupBy = undefined;
    this.set(GroupingProperties.groupBy, undefined);
    this.set(GroupingProperties.subgroupBy, undefined);
  }

  clearGroupBy(): void {
    this.#groupBy = undefined;
    this.set(GroupingProperties.groupBy, undefined);
  }

  clearSubgroupBy(): void {
    this.#subgroupBy = undefined;
    this.set(GroupingProperties.subgroupBy, undefined);
  }

  toJSON(): CollabGroupingJSON {
    return {
      groupBy: this.groupBy?.toJSON(),
      subgroupBy: this.subgroupBy?.toJSON()
    };
  }

  static fromJSON(groupingJSON: CollabGroupingJSON): YGrouping {
    const { groupBy, subgroupBy } = groupingJSON;

    return new YGrouping(
      new Y.Map([
        [
          GroupingProperties.groupBy,
          groupBy ? YFieldGrouping.fromJSON(groupBy).data : undefined
        ],
        [
          GroupingProperties.subgroupBy,
          subgroupBy ? YFieldGrouping.fromJSON(subgroupBy).data : undefined
        ]
      ])
    );
  }
}
